import { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    CircularProgress,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Stack,
    Typography,
} from "@mui/material";

import dayjs from "dayjs";
import * as yup from "yup";
import { useFormik } from "formik";

import { useSelector } from "react-redux";
import { RootState, store } from "@store/store";

import { Comment } from "@interfaces/application";
import { GeneralCommentsProps } from "@interfaces/slices";
import { useLazyGetModulesByCodeQuery } from "@services/api/roles";
import { useLazyGetCommentsByAppQuery, usePostCommentByAppMutation } from "@services/api/applications";

import "dayjs/locale/es-mx";
import duration from "dayjs/plugin/duration";

import SendIcon from "@mui/icons-material/Send";

dayjs.extend(duration);

export const ComentariosGenerales = (props: GeneralCommentsProps) => {
    const [comments, setComments] = useState<Array<Comment>>([]);

    const permissions = (state = store.getState()) => state.app.session.permissions;
    const userSession = useSelector((state: RootState) => state.app.session.user);
    const currentModule = useSelector((state: RootState) => state.components.currentSection);
    const applicationSelected = useSelector((state: RootState) => state.app.applicationSelected);

    const [triggerPostComment] = usePostCommentByAppMutation();
    const [triggerGetModulesByCode] = useLazyGetModulesByCodeQuery();

    const [triggerGetComments, getCommentsResponse] = useLazyGetCommentsByAppQuery();

    const formik = useFormik({
        initialValues: {
            comment: "",
        },
        validationSchema: yup.object().shape({
            comment: yup.string().required("El campo no puede estar vacio"),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                const item = document.querySelector("#scrollCommentItem");
                if (item) item.scrollTop = 0;

                const module = await triggerGetModulesByCode(currentModule.module).unwrap();

                await triggerPostComment({
                    applicationId: applicationSelected.id ?? "0-0-0-0-0",
                    payload: {
                        comment: values.comment,
                        user_id: userSession.id,
                        module_id: module?.id,
                    },
                }).unwrap();
            } catch (error) {
            } finally {
                formik.resetForm();
                await reloadInfo();
                setSubmitting(true);
            }
        },
    });

    const getElapsedTime = (pastDate: any) => {
        const pastDays = dayjs(pastDate).utc(true).local().locale("es-mx");
        const currentDate = dayjs().utc(false).local().locale("es-mx");

        const diff = dayjs.duration(currentDate.diff(pastDays));
        const days = diff.days();
        const hours = diff.hours();

        const label1 = days > 1 ? "días" : "día";
        const label2 = hours > 1 ? "horas" : "hora";

        return `Hace ${days} ${label1}, ${hours} ${label2}`;
    };

    const getInitials = (name: string, last_name: string, last_name_2: string) => {
        let initials = "";

        const names = [name, last_name, last_name_2];
        initials =
            names.length === 1 ? names[0] : (names[0]?.substring(0, 1) ?? "") + (names[1]?.substring(0, 1) ?? "");

        return initials ?? "";
    };

    const reloadInfo = async () => {
        try {
            const response = await triggerGetComments(applicationSelected.id ?? "0-0-0-0-0").unwrap();

            const tmp = [...response]
                .filter((comment) => comment.active === true)
                .sort((a: Comment, b: Comment) => (dayjs(a.created_at).isAfter(dayjs(b.created_at)) ? -1 : 1))
                .map((comment) => {
                    if (comment.module.code === "SBDOCEX") {
                        return { ...comment, comment: comment.comment.split("|").pop() };
                    } else if (comment.module.code === "SBREFS") {
                        return { ...comment, comment: JSON.parse(comment.comment).msg };
                    } else {
                        return comment;
                    }
                })
                .filter((comment) => permissions(store.getState()).some((t) => t.module.code === comment.module.code));

            setComments(tmp);
        } catch (error) {
            console.log(error);
        } finally {
        }
    };

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0" || applicationSelected.id === undefined) return;

        reloadInfo();
    }, [applicationSelected.id]);

    return (
        <Box maxHeight={"800px"} mt={5} mb={5}>
            <Box id="scrollCommentItem" maxHeight={"800px"} overflow={"auto"}>
                <Box
                    display={formik.isSubmitting || getCommentsResponse.isFetching ? "flex" : "none"}
                    justifyContent={"center"}
                >
                    <CircularProgress />
                </Box>
                <Grid container direction={"column"} m={1}>
                    {comments?.map((comment) => (
                        <Grid key={comment.id} container px={1} size={12}>
                            <Grid size={2.2}>
                                <Stack borderRight={"0.5px solid #A3D4E8"} pt={4} px={1} rowGap={1}>
                                    <Grid container columnGap={2}>
                                        <Avatar sx={{ bgcolor: comment.user.avatar_color }}>
                                            {getInitials(
                                                comment.user.name,
                                                comment.user.last_name,
                                                comment.user.last_name_2,
                                            )}
                                        </Avatar>
                                        <Stack>
                                            <Typography variant="body1" color="main3">
                                                {comment.user.name} {comment.user.last_name}
                                            </Typography>
                                            <Typography variant="body2" color="main3" sx={{ opacity: 0.85 }}>
                                                Customer Journey
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    <Grid textAlign={"right"}>
                                        <Typography variant="body1" color="main3" sx={{ opacity: 0.85 }}>
                                            {dayjs(comment.created_at)
                                                .utc(true)
                                                .local()
                                                .locale("es-mx")
                                                .format("MMM DD - hh:MM a")}
                                        </Typography>
                                        <Typography variant="body2" color="main3" sx={{ opacity: 0.85 }}>
                                            {getElapsedTime(comment.created_at)}
                                        </Typography>
                                    </Grid>
                                </Stack>
                            </Grid>
                            <Grid px={1} size={"grow"} pt={4}>
                                <Typography variant="body1" component={"span"} fontWeight={500} color="main3">
                                    {comment.module.name} -{" "}
                                    <Typography variant="body1" component={"span"} color="main3">
                                        {/* {comment.module.code === "SBDOCEX" && comment.comment.split("|").pop()} */}
                                        {/* {comment.module.code === "SBREFS" && comment.} */}
                                        {comment.module.code === "SBDOCEX"
                                            ? comment.comment.split("|").pop()
                                            : comment.comment}
                                    </Typography>
                                </Typography>
                            </Grid>
                        </Grid>
                    ))}
                    <Grid container px={1} size={12}>
                        <Grid size={2.2} borderRight={"0.5px solid #A3D4E8"}>
                            <Stack>
                                <Grid></Grid>
                            </Stack>
                        </Grid>
                        <Grid px={1} size={"grow"}>
                            <form
                                onSubmit={formik.handleSubmit}
                                noValidate
                                hidden={
                                    permissions(store.getState()).find((p) => p.module.code === "MCOGRAL")?.can_edit ===
                                    false
                                }
                            >
                                <FormControl variant="outlined" fullWidth>
                                    <InputLabel
                                        htmlFor="comment"
                                        error={formik.touched.comment && Boolean(formik.errors.comment)}
                                    >
                                        Agrega un comentario
                                    </InputLabel>
                                    <OutlinedInput
                                        id="comment"
                                        type="text"
                                        label="Agrega un comentario"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.comment}
                                        error={formik.touched.comment && Boolean(formik.errors.comment)}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    type="submit"
                                                    disabled={
                                                        !formik.dirty ||
                                                        !formik.isValid ||
                                                        !(Object.keys(formik.errors).length === 0)
                                                    }
                                                >
                                                    <SendIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                    {formik.touched.comment && Boolean(formik.errors.comment) && (
                                        <FormHelperText>{formik.errors.comment}</FormHelperText>
                                    )}
                                </FormControl>
                            </form>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};
